import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MessageList from './components/MessageList';
import MessageInput from './components/MessageInput';
import UserInfo from './components/UserInfo';
import Auth from './components/Auth';
import Thread from './components/Thread';
import ReminderModal from './components/ReminderModal';
import ReminderNotification from './components/ReminderNotification';
import WorkspaceSwitcher from './components/WorkspaceSwitcher';
import RegisterContact from './components/RegisterContact';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import SetupGuide from './components/SetupGuide';
import { useAuth } from './hooks/useAuth';
import { useSlackData } from './hooks/useSlackData';
import socketService from './services/socket';
import { api } from './services/api';
import { getHubSpotContext } from './utils/hubspotContext';
import './App.css';

function SlackApp() {
  const { userToken, isAuthenticated, loading: authLoading, login, logout, currentWorkspace, switchWorkspace } = useAuth();
  const {
    channels,
    selectedChannel,
    messages,
    users,
    loading,
    selectChannel,
    sendMessage,
    selectedThread,
    threadReplies,
    threadLoading,
    selectThread,
    closeThread,
    sendThreadReply,
    editMessage,
    deleteMessage,
    currentUserId,
    addReaction,
    removeReaction,
    starredItems,
    reminders,
    lastReadTimestamps,
    channelError,
    clearChannelError
  } = useSlackData(userToken, isAuthenticated, currentWorkspace);

  const [socketConnected, setSocketConnected] = useState(true);
  const [reminderModal, setReminderModal] = useState({ isOpen: false, message: null });
  const [hubspotSyncEnabled, setHubspotSyncEnabled] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('hubspot_sync_enabled');
    return saved === 'true';
  });

  // Get HubSpot context for portal ID
  const hubspotContext = getHubSpotContext();

  // Handle HubSpot sync toggle
  const handleHubspotSyncToggle = async (enabled) => {
    setHubspotSyncEnabled(enabled);
    localStorage.setItem('hubspot_sync_enabled', enabled.toString());

    // Update socket sync settings for real-time incoming message sync
    if (enabled && hubspotContext?.portalId && hubspotContext?.contactId) {
      socketService.updateSyncSettings({
        enabled: true,
        portalId: hubspotContext.portalId,
        contactId: hubspotContext.contactId,
        channelName: selectedChannel?.name || ''
      });
    } else {
      socketService.updateSyncSettings({ enabled: false });
    }

    // Save preference to backend if we have HubSpot context
    if (hubspotContext?.portalId) {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        await fetch(`${backendUrl}/api/settings/hubspot-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userToken}`
          },
          credentials: 'include',
          body: JSON.stringify({
            hubspotPortalId: hubspotContext.portalId,
            hubspotUserId: hubspotContext.userId,
            syncEnabled: enabled
          })
        });
        console.log('HubSpot sync preference saved:', enabled);
      } catch (error) {
        console.error('Error saving HubSpot sync preference:', error);
      }
    }
  };

  const [needsHubspotAuth, setNeedsHubspotAuth] = useState(false);

  // Check HubSpot token status on load
  useEffect(() => {
    const checkHubspotTokens = async () => {
      if (!hubspotContext?.portalId) return;

      // Skip if we just connected (URL has hubspotConnected param)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('hubspotConnected') === 'true') {
        // Clean up URL
        urlParams.delete('hubspotConnected');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
        setNeedsHubspotAuth(false);
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(
          `${backendUrl}/api/settings/hubspot-token-status?hubspotPortalId=${hubspotContext.portalId}`,
          { credentials: 'include' }
        );
        const data = await response.json();

        if (!data.hasTokens) {
          console.log('⚠️ No HubSpot tokens found, need authorization');
          setNeedsHubspotAuth(true);
        } else {
          setNeedsHubspotAuth(false);
        }
      } catch (error) {
        console.error('Error checking HubSpot token status:', error);
      }
    };

    checkHubspotTokens();
  }, [hubspotContext?.portalId]);

  // Open HubSpot OAuth in popup window
  const handleHubspotAuth = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    const oauthUrl = `${backendUrl}/api/hubspot-oauth?` +
      `portalId=${hubspotContext?.portalId || ''}` +
      `&userId=${hubspotContext?.userId || ''}` +
      `&contactId=${hubspotContext?.contactId || ''}` +
      `&popup=true`;

    // Open popup window
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      oauthUrl,
      'hubspot-oauth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );

    // Listen for OAuth completion message
    const handleMessage = (event) => {
      if (event.data?.type === 'HUBSPOT_AUTH_SUCCESS') {
        console.log('✅ HubSpot OAuth completed');
        setNeedsHubspotAuth(false);
        window.removeEventListener('message', handleMessage);
        if (popup && !popup.closed) popup.close();
      }
    };

    window.addEventListener('message', handleMessage);
  };

  // Load HubSpot sync preference from backend on mount
  useEffect(() => {
    const loadHubspotSyncPreference = async () => {
      if (hubspotContext?.portalId && userToken) {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
          const response = await fetch(
            `${backendUrl}/api/settings/hubspot-sync?hubspotPortalId=${hubspotContext.portalId}&hubspotUserId=${hubspotContext.userId}`,
            {
              headers: {
                'Authorization': `Bearer ${userToken}`
              },
              credentials: 'include'
            }
          );
          const data = await response.json();
          if (data.success && data.syncEnabled !== undefined) {
            setHubspotSyncEnabled(data.syncEnabled);
            localStorage.setItem('hubspot_sync_enabled', data.syncEnabled.toString());
          }
        } catch (error) {
          console.error('Error loading HubSpot sync preference:', error);
        }
      }
    };

    if (isAuthenticated) {
      loadHubspotSyncPreference();
    }
  }, [isAuthenticated, hubspotContext?.portalId, userToken]);

  useEffect(() => {
    if (isAuthenticated) {
      const socket = socketService.connect();

      // Listen for connection status changes
      socket.on('connect', () => {
        console.log('Socket connected');
        setSocketConnected(true);
      });

      socket.on('disconnect', () => {
        console.log('Socket disconnected');
        setSocketConnected(false);
      });

      socket.on('connect_error', () => {
        console.log('Socket connection error');
        setSocketConnected(false);
      });
    } else {
      socketService.disconnect();
    }

    return () => {
      socketService.disconnect();
    };
  }, [isAuthenticated]);

  const handleBookmark = async (message, isBookmarked) => {
    try {
      if (isBookmarked) {
        await api.addBookmark(selectedChannel.id, message.ts, userToken);
      } else {
        await api.removeBookmark(selectedChannel.id, message.ts, userToken);
      }
      console.log(`Message ${isBookmarked ? 'bookmarked' : 'unbookmarked'} successfully`);

      // Immediately fetch updated starred items to refresh UI
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(`${backendUrl}/api/bookmarks/list?token=${encodeURIComponent(userToken)}`);
        const data = await response.json();

        if (data.success && data.items) {
          // Update messages with latest starred status
          const starredMessageTimestamps = new Set(
            data.items
              .filter(item => item.type === 'message')
              .map(item => item.message?.ts)
              .filter(Boolean)
          );

          // Force a re-render by updating the messages in useSlackData
          // This will be handled by the polling, but we can also update immediately
          window.dispatchEvent(new CustomEvent('bookmark-updated', {
            detail: { starredTimestamps: Array.from(starredMessageTimestamps) }
          }));
        }
      } catch (refreshError) {
        console.error('Error refreshing bookmarks:', refreshError);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      alert('Failed to update bookmark. Please try again.');
    }
  };

  const handleRemind = (message) => {
    setReminderModal({ isOpen: true, message });
  };

  const handleSetReminder = async (message, time) => {
    try {
      const response = await api.addReminder(message.text, time, userToken);
      console.log('Reminder set successfully', response);

      // Auto-bookmark the message when reminder is set (like Slack does)
      if (!message.is_starred) {
        await api.addBookmark(selectedChannel.id, message.ts, userToken);
      }

      // Trigger immediate refresh of bookmarks and reminders
      window.dispatchEvent(new CustomEvent('bookmark-updated'));
      window.dispatchEvent(new CustomEvent('reminder-updated'));

      closeReminderModal();
    } catch (error) {
      console.error('Error setting reminder:', error);
      alert('Failed to set reminder. Please try again.');
    }
  };

  const closeReminderModal = () => {
    setReminderModal({ isOpen: false, message: null });
  };

  const handleWorkspaceChange = async (workspace) => {
    await switchWorkspace(workspace);
    // Data will auto-refresh via useSlackData dependency on currentWorkspace
  };

  // Show nothing while checking auth to prevent flash
  if (authLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Auth onAuthenticated={login} />;
  }

  // Show HubSpot authorization prompt if needed
  if (needsHubspotAuth) {
    return (
      <div className="hubspot-auth-prompt">
        <div className="hubspot-auth-content">
          <h2>Connect HubSpot</h2>
          <p>To sync your Slack messages to HubSpot timeline, we need to connect to your HubSpot account.</p>
          <button onClick={handleHubspotAuth} className="hubspot-auth-button">
            Connect HubSpot
          </button>
          <button onClick={() => setNeedsHubspotAuth(false)} className="hubspot-skip-button">
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <ReminderNotification reminders={reminders} />
      {!socketConnected && (
        <div className="connection-banner">
          <span>⚠️ Disconnected - Attempting to reconnect...</span>
        </div>
      )}
      <div className="header">
        <div className="header-left">
          <h2>Slack Extension</h2>
          <WorkspaceSwitcher
            currentWorkspace={currentWorkspace}
            onWorkspaceChange={handleWorkspaceChange}
            token={userToken}
          />
          <div className="channel-selector">
            <select
              value={selectedChannel?.id || ''}
              onChange={(e) => {
                const channel = channels.find(ch => ch.id === e.target.value);
                if (channel) selectChannel(channel);
              }}
              className="channel-dropdown"
            >
              <option value="" disabled>Select a channel</option>
              {channels.map(channel => (
                <option key={channel.id} value={channel.id}>
                  {channel.is_private ? '🔒' : '#'} {channel.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="header-right">
          <label className="hubspot-sync-toggle">
            <input
              type="checkbox"
              checked={hubspotSyncEnabled}
              onChange={(e) => handleHubspotSyncToggle(e.target.checked)}
            />
            <span className="hubspot-sync-label">Sync to HubSpot</span>
          </label>
        </div>
      </div>
      <div className="main-area">
        <div className="main-content">
          {selectedChannel ? (
            <>
              <MessageList
                messages={messages}
                users={users}
                loading={loading}
                onThreadClick={selectThread}
                onEditMessage={editMessage}
                onDeleteMessage={deleteMessage}
                currentUserId={currentUserId}
                onAddReaction={addReaction}
                onRemoveReaction={removeReaction}
                onBookmark={handleBookmark}
                onRemind={handleRemind}
                channelId={selectedChannel.id}
                lastReadTimestamp={lastReadTimestamps[selectedChannel.id]}
                channelError={channelError}
              />
              <MessageInput
                onSendMessage={sendMessage}
                users={users}
                channelId={selectedChannel.id}
                channelName={selectedChannel.name}
                token={userToken}
              />
            </>
          ) : (
            <div className="no-channel-selected">
              <p>Select a channel to start messaging</p>
            </div>
          )}
        </div>
        {selectedThread && (
          <Thread
            threadMessage={selectedThread}
            threadReplies={threadReplies}
            users={users}
            onClose={closeThread}
            onSendReply={sendThreadReply}
            loading={threadLoading}
            onEditMessage={editMessage}
            onDeleteMessage={deleteMessage}
            currentUserId={currentUserId}
            onAddReaction={addReaction}
            onRemoveReaction={removeReaction}
            onBookmark={handleBookmark}
            onRemind={handleRemind}
            token={userToken}
            channelId={selectedChannel?.id}
          />
        )}
      </div>
      <ReminderModal
        isOpen={reminderModal.isOpen}
        onClose={closeReminderModal}
        onSetReminder={handleSetReminder}
        message={reminderModal.message}
        users={users}
      />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/register-contact" element={<RegisterContact />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/setup-guide" element={<SetupGuide />} />
      <Route path="/app/*" element={<SlackApp />} />
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

export default App;